import {get, set} from '@servicenow/ui-utils';
import {
	NOW_UI_CONFIG,
	NOW_UI_FRAMEWORK,
	NOW_UI_FRAMEWORK_CONFIG_KEYS,
	NXUI_SYS_PROPS
} from './constants';
import {default as emitNowUiEvent} from './emitNowUiEvent';
import {getConfigForEnv} from './configs/index';

const configInstance = {};

/***
 * Private
 */

/***
 * @function getConfigFromGlobal
 * @description checks the globalThis object for configs
 */
const getConfigFromGlobal = () => {
	const globalConfig = {};

	if (!globalThis[NOW_UI_FRAMEWORK]) return globalConfig;

	NOW_UI_FRAMEWORK_CONFIG_KEYS.forEach(key => {
		if (globalThis[NOW_UI_FRAMEWORK][key] !== undefined)
			globalConfig[key] = globalThis[NOW_UI_FRAMEWORK][key];
	});

	return globalConfig;
};

const getConfigFromSysProps = () => {
	const nxuiConfig = {};

	if (!globalThis.ux_globals?.nxui?.sysprops) return nxuiConfig;

	const sysPropKeys = Object.keys(NXUI_SYS_PROPS);
	for (const sysProp in globalThis.ux_globals.nxui.sysprops) {
		if (sysPropKeys.indexOf(sysProp) > -1) {
			const configKey = NXUI_SYS_PROPS[sysProp];
			const sysPropValue = globalThis.ux_globals.nxui.sysprops[sysProp];
			if (sysPropValue === 'true') nxuiConfig[configKey] = true;
			else if (sysPropValue === 'false') nxuiConfig[configKey] = false;
			else nxuiConfig[configKey] = sysPropValue;
		}
	}

	return nxuiConfig;
};

const observeredProperties = {};

/***
 * Public
 */

/**
 * getConfig
 * @description returns the config instance
 * @function getConfig
 */
export const getConfig = () => {
	// Need to whitelist keys
	const globalConfig = getConfigFromGlobal();
	const sysPropConfig = getConfigFromSysProps();
	const envConfig = getConfigForEnv();

	// Build Config Object -> Env Defaults -> Sys Props -> Global Config -> Overrides
	const configObj = {...envConfig, ...sysPropConfig, ...globalConfig, ...configInstance};
	return configObj;
};

/**
 * observeConfigItem
 * @description returns a function to remove the observer
 * @param {String} path
 * @param {Function} fn
 * @function observeConfigItem
 */
export const observeConfigItem = (path, fn) => {
	const observers = observeredProperties[path] || (observeredProperties[path] = new Set());
	observers.add(fn);
	return () => observers.delete(fn);
};

/**
 * Getter for Config
 * @param {String} path
 * @returns {Object} Returns the key and value for the config item
 */
export const getConfigItem = path => {
	const overridden = get(configInstance, path);
	if (overridden !== undefined) return overridden;

	const globalVar = get(getConfigFromGlobal(), path);
	if (globalVar !== undefined) return globalVar;

	const sysPropVar = get(getConfigFromSysProps(), path);
	if (sysPropVar !== undefined) return sysPropVar;

	return get(getConfigForEnv(), path, undefined);
};

/**
 * Setter for Config
 * @function setConfigItem
 * @description Setter for the Seismic config
 * @param {String} path
 * @param {*} value
 * @returns {Object} Returns the key and value for the config item that was set
 *
 * Is it a junk drawer or do we want to whitelist keys?
 */
export const setConfigItem = (path, value) => {
	if (getConfigItem(path) === value) return;

	emitNowUiEvent(NOW_UI_CONFIG, {path, value, previousValue: getConfigItem(path)});
	const retVal = set(configInstance, path, value);
	const observers = observeredProperties[path];
	if (observers) observers.forEach(fn => fn(path, value));

	return retVal;
};
